import { Channel } from "phoenix";
import { IAppMessageRecipientData } from "common/records/AppMessage";

/** Counts from `AppMessages.get_metadata/2`; the page fields ride only on an `app_message:list` reply. */
export interface IAppMessageMetadata {
  total: number;
  unread_total: number;
  page_number?: number;
  per_page?: number;
  total_pages?: number;
}

export interface IAppMessageResponse {
  app_messages: IAppMessageRecipientData[];
  metadata: IAppMessageMetadata;
}

export interface IAppMessageUpdateResponse {
  app_message: IAppMessageRecipientData;
  metadata: IAppMessageMetadata;
}

export interface IAppMessageHandlers {
  onInitialMessages: (messages: IAppMessageRecipientData[], metadata: IAppMessageMetadata) => void;
  onNewMessage: (message: IAppMessageRecipientData, metadata: IAppMessageMetadata) => void;
  onMessageUpdate: (message: IAppMessageRecipientData, metadata: IAppMessageMetadata) => void;
}

const TIMEOUT_ERROR = "Request timeout";

const push = <T>(channel: Channel, event: string, payload: Record<string, unknown> = {}): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    channel
      .push(event, payload)
      .receive("ok", (response: T) => resolve(response))
      .receive("error", (error: unknown) => reject(error))
      .receive("timeout", () => reject(new Error(TIMEOUT_ERROR)));
  });

export const setupChannelListeners = (channel: Channel, handlers: IAppMessageHandlers): Channel => {
  channel.on("app_message:new", (payload: IAppMessageUpdateResponse) => {
    handlers.onNewMessage(payload.app_message, payload.metadata);
  });

  channel.on("app_message:updated", (payload: IAppMessageUpdateResponse) => {
    handlers.onMessageUpdate(payload.app_message, payload.metadata);
  });

  return channel;
};

export const handleJoinResponse = (response: IAppMessageResponse, handlers: IAppMessageHandlers): void => {
  handlers.onInitialMessages(response?.app_messages ?? [], response?.metadata ?? { total: 0, unread_total: 0 });
};

export const listMessages = (channel: Channel, page = 1, pageSize = 20): Promise<IAppMessageResponse> =>
  push<IAppMessageResponse>(channel, "app_message:list", { page, page_size: pageSize });

export const markRead = (channel: Channel, id: string): Promise<void> => push(channel, "app_message:read", { id });

export const markAction = (channel: Channel, id: string): Promise<void> => push(channel, "app_message:action", { id });

export const dismissMessage = (channel: Channel, id: string): Promise<void> =>
  push(channel, "app_message:dismissed", { id });

export const archiveMessage = (channel: Channel, id: string): Promise<void> =>
  push(channel, "app_message:archive", { id });
